/**
 * Le passage de la confirmation d'une réservation faite depuis le site d'un
 * réparateur à la page « Créer mon espace » (`/creer-mon-espace`). Pur, sans
 * React.
 *
 * Le `public_token` de la demande voyage dans le FRAGMENT de l'adresse
 * (`#s=<slug>&t=<jeton>`), jamais dans la requête : le fragment n'est pas
 * envoyé au serveur, donc absent des journaux Vercel. Il repart en revanche
 * dans le chemin de la lecture `GET …/site/bookings/{jeton}` (journaux d'accès
 * de l'api, comme `confirm-code`), et Sentry le masque
 * (`utils/sentryFilters.jsx`). La page le lit puis l'efface de la
 * barre d'adresse. Le slug l'accompagne parce que l'api n'ouvre une demande
 * que par le couple slug + jeton (isolation par atelier).
 */

export const CHEMIN_ESPACE = '/creer-mon-espace';

const JETON = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG = /^[\w-]{1,100}$/;

/** L'adresse de la page, avec le couple slug + jeton quand on les a tous deux. */
export function lienCreerMonEspace({ slug, token } = {}) {
  if (!slug || !token) return CHEMIN_ESPACE;
  const fragment = new URLSearchParams({ s: slug, t: token }).toString();
  return `${CHEMIN_ESPACE}#${fragment}`;
}

/**
 * Lit le couple `{ slug, token }` du fragment de l'adresse courante, puis
 * retire le fragment de la barre d'adresse (sans recharger, sans nouvelle
 * entrée d'historique). `null` si le fragment est absent ou mal formé, ce qui
 * ouvre la page en version générique. Le fragment est retiré dans tous les cas.
 */
export function lireEtEffacerFragment() {
  if (typeof window === 'undefined') return null;
  const { hash, pathname, search } = window.location;
  if (!hash || hash === '#') return null;

  let lu = null;
  try {
    const params = new URLSearchParams(hash.slice(1));
    const slug = params.get('s');
    const token = params.get('t');
    if (slug && token && SLUG.test(slug) && JETON.test(token)) {
      lu = { slug, token };
    }
  } catch (e) {
    lu = null;
  }

  try {
    window.history.replaceState(window.history.state, '', pathname + search);
  } catch (e) {
    // Historique refusé (cadre isolé) : la page fonctionne, le fragment reste.
  }
  return lu;
}

/**
 * Un numéro tel que l'atelier l'a reçu (« 06 12 34 56 78 », « 06.12.34.56.78 »)
 * sous la forme que le formulaire d'inscription accepte, sans séparateurs.
 */
export const telPourInscription = (tel) =>
  (tel || '').replace(/[\s.\-()]/g, '');

/**
 * Les valeurs de départ du formulaire d'inscription, d'après la demande que
 * l'api rend pour ce jeton (`customer: { name, email, phone }`). L'api ne
 * garde qu'un nom : le premier mot est le prénom, le reste le nom, et le client
 * corrige au besoin. Sans demande, un objet vide.
 */
export function valeursInscription(demande) {
  const customer = demande && demande.customer;
  if (!customer) return {};
  const nomComplet = (customer.name || '').trim().replace(/\s+/g, ' ');
  const coupe = nomComplet.indexOf(' ');
  const prenom = coupe === -1 ? nomComplet : nomComplet.slice(0, coupe);
  const nom = coupe === -1 ? '' : nomComplet.slice(coupe + 1);
  return {
    prenom,
    nom,
    tel: telPourInscription(customer.phone),
    email: (customer.email || '').trim(),
  };
}
